import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="youtube-player"
// Simple YouTube player controller that loads videos into an iframe
export default class extends Controller {
  static values = {
    videoId: String,
    width: { type: String, default: "432" },
    height: { type: String, default: "243" },
    volume: { type: Number, default: 25 },
    quality: { type: String, default: "hd720" },
    color: { type: String, default: "white" },
    rel: { type: Number, default: 0 },
    modestbranding: { type: Number, default: 1 }
  }

  connect() {
    this.playerCreating = false // Flag to prevent concurrent player creation

    // Get the player element (child div that will be replaced by YouTube API)
    this.playerElement = this.element.querySelector('#player') || this.element

    console.log('[youtube-player] Controller connected', {
      videoId: this.videoIdValue,
      controllerElementId: this.element.id,
      playerElementId: this.playerElement.id,
      playerElementTagName: this.playerElement.tagName
    })

    // Check if player already exists (from a previous connection or AJAX update)
    // The YouTube API replaces the #player div with an iframe
    const existingIframe = this.playerElement.tagName === 'IFRAME'
      ? this.playerElement
      : this.element.querySelector('iframe[src*="youtube.com"]')

    // If we see an iframe, a player was already created - don't create another one
    if (existingIframe) {
      console.log('[youtube-player] Found existing YouTube iframe, attempting to get player', {
        hasYT: !!window.YT,
        elementId: this.element.id
      })

      if (window.YT && window.YT.get) {
        try {
          // Use the player element's ID (which becomes the iframe's ID)
          const playerId = this.playerElement.id || 'player'
          this.player = window.YT.get(playerId)
          if (this.player) {
            console.log('[youtube-player] Reconnected and found existing player', {
              playerId,
              hasPlayer: !!this.player
            })
            // Player reference restored - just set up event listener and return
            this.boundHandleLoadVideo = this.handleLoadVideoEvent.bind(this)
            this.element.addEventListener('youtube:load-video', this.boundHandleLoadVideo)
            return
          }
        } catch (e) {
          console.log('[youtube-player] Could not get existing player on connect:', e)
        }
      }

      // Iframe exists but couldn't get player reference - still don't create a new one
      // The player exists, we just can't get the reference yet
      console.log('[youtube-player] Iframe exists but player reference not available yet - skipping load')
      this.player = null
      this.boundHandleLoadVideo = this.handleLoadVideoEvent.bind(this)
      this.element.addEventListener('youtube:load-video', this.boundHandleLoadVideo)
      return
    }

    // No existing player found
    this.player = null

    // Listen for custom event to load video
    this.boundHandleLoadVideo = this.handleLoadVideoEvent.bind(this)
    this.element.addEventListener('youtube:load-video', this.boundHandleLoadVideo)

    // Only load video on initial connect if videoId is provided and no iframe exists
    // Don't auto-load on reconnect to prevent loops
    if (this.videoIdValue && !existingIframe) {
      this.loadVideo(this.videoIdValue)
    }
  }

  disconnect() {
    // Remove event listener
    if (this.boundHandleLoadVideo) {
      this.element.removeEventListener('youtube:load-video', this.boundHandleLoadVideo)
    }

    // Clean up player
    if (this.player) {
      try {
        this.player.destroy()
      } catch (e) {
        // Ignore errors during cleanup
      }
      this.player = null
    }
  }

  handleLoadVideoEvent(event) {
    const videoId = event.detail?.videoId
    if (videoId) {
      console.log('[youtube-player] Received load video event', { videoId })
      this.loadVideo(videoId)
    }
  }

  // Public method to load a video - can be called directly from JavaScript
  loadVideo(videoId) {
    if (!videoId || videoId.trim() === '') {
      console.warn('[youtube-player] loadVideo called with empty video ID')
      return
    }

    // Ensure we have the player element reference
    if (!this.playerElement) {
      this.playerElement = this.element.querySelector('#player') || this.element
    }

    console.log('[youtube-player] loadVideo called', {
      videoId,
      hasPlayer: !!this.player,
      playerElementTagName: this.playerElement.tagName
    })

    // Only update attribute if player doesn't exist yet
    // If player exists, we're just changing the video, not creating a new player
    if (!this.player) {
      this.element.setAttribute('data-youtube-player-video-id-value', videoId)
    }

    // If player exists, just load the new video
    if (this.player) {
      try {
        this.player.loadVideoById(videoId)
        console.log('[youtube-player] Video loaded on existing player')
      } catch (error) {
        console.error('[youtube-player] Error loading video:', error)
      }
      return
    }

    // Player doesn't exist, need to create it
    // First ensure YouTube API is loaded
    if (!window.YT) {
      this.loadYouTubeAPI(() => {
        this.createPlayer(videoId)
      })
    } else if (window.YT.Player) {
      // API is ready, create player
      this.createPlayer(videoId)
    } else {
      // API is loading, wait for it
      const originalCallback = window.onYouTubeIframeAPIReady
      window.onYouTubeIframeAPIReady = () => {
        if (originalCallback) originalCallback()
        this.createPlayer(videoId)
      }
    }
  }

  loadYouTubeAPI(callback) {
    // Prevent multiple API loads
    if (window.YT) {
      if (callback) callback()
      return
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]')
    if (existingScript) {
      // Script is already loading, just set up callback
      const originalCallback = window.onYouTubeIframeAPIReady
      window.onYouTubeIframeAPIReady = () => {
        if (originalCallback) originalCallback()
        if (callback) callback()
      }
      return
    }

    // Load the script
    const tag = document.createElement("script")
    tag.src = "https://www.youtube.com/iframe_api"
    const firstScriptTag = document.getElementsByTagName("script")[0]
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)

    // Set up callback (preserve any existing callback)
    const originalCallback = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      if (originalCallback) originalCallback()
      if (callback) callback()
    }
  }

  createPlayer(videoId) {
    // Prevent concurrent player creation
    if (this.playerCreating) {
      console.warn('[youtube-player] Player creation already in progress, skipping')
      return
    }

    // Ensure we have the player element reference
    if (!this.playerElement) {
      this.playerElement = this.element.querySelector('#player') || this.element
    }

    // Check if player already exists (maybe from a previous connection)
    if (this.player) {
      console.log('[youtube-player] Player already exists, loading video')
      this.player.loadVideoById(videoId)
      return
    }

    // CRITICAL: Check for existing iframe FIRST - if it exists, NEVER create a new player
    // The YouTube API replaces the #player div with an iframe
    const existingIframe = this.playerElement.tagName === 'IFRAME'
      ? this.playerElement
      : this.element.querySelector('iframe[src*="youtube.com"]')

    if (existingIframe) {
      console.log('[youtube-player] Iframe already exists - player was already created', {
        iframeSrc: existingIframe.src || 'N/A',
        playerElementId: this.playerElement.id
      })

      // Try to get the existing player
      try {
        const playerId = this.playerElement.id || 'player'
        if (playerId && window.YT && window.YT.get) {
          this.player = window.YT.get(playerId)
          if (this.player) {
            console.log('[youtube-player] Got existing player, loading video')
            this.player.loadVideoById(videoId)
            return
          }
        }
      } catch (e) {
        console.error('[youtube-player] Error getting existing player:', e)
      }

      // Iframe exists but couldn't get player - DON'T create a new one
      // This prevents infinite loops
      console.warn('[youtube-player] Iframe exists but cannot get player - not creating duplicate')
      return
    }

    // Create new player per YouTube API documentation
    // Reference: https://developers.google.com/youtube/iframe_api_reference
    if (!window.YT || !window.YT.Player) {
      console.error('[youtube-player] YouTube API not available')
      return
    }

    // Set flag before creating
    this.playerCreating = true

    console.log('[youtube-player] Creating new YouTube player', {
      videoId,
      width: this.widthValue,
      height: this.heightValue,
      playerElementId: this.playerElement.id
    })

    try {
      // Per API docs: new YT.Player(element, options)
      // Use the playerElement (child div), not the controller element
      // The API will replace the #player div with an iframe
      this.player = new window.YT.Player(this.playerElement, {
        width: this.widthValue,
        height: this.heightValue,
        videoId: videoId,
        playerVars: {
          // Note: showinfo is deprecated, but keeping for backward compatibility
          color: this.colorValue,
          rel: this.relValue,
          modestbranding: this.modestbrandingValue
        },
        events: {
          onReady: (event) => {
            console.log('[youtube-player] Player ready')
            this.playerCreating = false // Clear flag when player is ready
            // Set volume and quality when player is ready
            try {
              event.target.setVolume(this.volumeValue)
              event.target.setPlaybackQuality(this.qualityValue)
            } catch (e) {
              console.warn('[youtube-player] Error setting volume/quality:', e)
            }
          },
          onError: (event) => {
            console.error('[youtube-player] Player error:', event.data)
            this.playerCreating = false // Clear flag on error
          }
        }
      })
      console.log('[youtube-player] YouTube Player instance created')
    } catch (error) {
      console.error('[youtube-player] Error creating player:', error)
      this.playerCreating = false // Clear flag on error
    }
  }
}
